import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import AdminLogin from './AdminLogin';
import Dashboard from './Dashboard';
import CoupangTool from './CoupangTool';
import OrderManagement from './orders/OrderManagement';
import DailyOrders from './orders/DailyOrders';
import LedgerHistory from './ledger/LedgerHistory';
import BlueberryFarm from './BlueberryFarm';
import ImageOptimizer from './ImageOptimizer';
import TaxInvoiceManager from './tax/TaxInvoiceManager';
import VatFiling from './tax/VatFiling';
import TransactionStatement from './transaction/TransactionStatement';
import VendorPage from './vendor/VendorPage';
import ShippingCalendar from './shipping-calendar/ShippingCalendar';
import SettlementPage from './settlement/SettlementPage';
import SundumbiSettlement from './settlement/sundumbi/SundumbiSettlement';
import EnvelopeDesigner from './envelope/EnvelopeDesigner';
import { UploadedFilesProvider } from './UploadedFilesContext';
import { VENDORS } from './vendor/vendors';

const menuGroups = [
  {
    label: '주문서 관리',
    items: [
      { path: '/admin/orders', label: '스마트스토어 전체 주문서' },
      { path: '/admin/daily-orders', label: '오늘의 주문서' },
    ],
  },
  {
    label: '파트너',
    items: VENDORS.map(v => ({ path: `/admin/${v.key}`, label: v.name })),
  },
  {
    label: '플랫폼',
    items: [
      { path: '/admin/coupang', label: '쿠팡 상세페이지' },
    ],
  },
  {
    label: '공통',
    items: [
      { path: '/admin/images', label: '이미지 최적화' },
      { path: '/admin/tax', label: '세금계산서 관리' },
      { path: '/admin/vat', label: '부가세 신고' },
      { path: '/admin/transaction', label: '거래내역서 생성' },
    ],
  },
  {
    label: '디자인',
    items: [
      { path: '/admin/shipping-calendar', label: '배송 달력' },
      { path: '/admin/envelope', label: '나눔 봉투' },
    ],
  },
  {
    label: '월말정산',
    items: [
      ...VENDORS.filter(v => v.settlement).map(v => ({ path: `/admin/settlement/${v.key}`, label: v.name })),
      { path: '/admin/settlement/sundumbi', label: '순수한둠비' },
    ],
  },
  {
    label: '장부',
    items: [
      { path: '/admin/ledger-history', label: '공급가 히스토리' },
    ],
  },
];

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30분
const TOKEN_KEY = 'admin-token';

function AdminLayout() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  // 페이지 로드 시 토큰 유효성 확인
  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }

    fetch('/api/verify', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (res.ok) {
          setIsAuthenticated(true);
          sessionStorage.setItem('admin-last-activity', String(Date.now()));
        } else {
          sessionStorage.removeItem(TOKEN_KEY);
        }
      })
      .catch(() => {
        // 서버 연결 안 될 때 (로컬 개발 등) — 토큰 있으면 통과
        setIsAuthenticated(true);
      })
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem('admin-last-activity');
    setIsAuthenticated(false);
    navigate('/admin');
  }, [navigate]);

  const handleLogin = (token: string) => {
    sessionStorage.setItem(TOKEN_KEY, token);
    sessionStorage.setItem('admin-last-activity', String(Date.now()));
    setIsAuthenticated(true);
  };

  // 미활동 자동 로그아웃
  useEffect(() => {
    if (!isAuthenticated) return;

    const updateActivity = () => {
      sessionStorage.setItem('admin-last-activity', String(Date.now()));
    };

    const checkIdle = () => {
      const lastActivity = parseInt(sessionStorage.getItem('admin-last-activity') || '0', 10);
      if (lastActivity && Date.now() - lastActivity > IDLE_TIMEOUT) {
        handleLogout();
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, updateActivity));
    updateActivity();

    const interval = setInterval(checkIdle, 60000);

    return () => {
      events.forEach(e => window.removeEventListener(e, updateActivity));
      clearInterval(interval);
    };
  }, [isAuthenticated, handleLogout]);

  if (checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-400 text-sm">확인 중...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <UploadedFilesProvider>
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <a href="/" className="text-lg font-bold text-gray-800 hover:text-jeju-brown transition-colors">
            제주로부터
          </a>
          <p className="text-xs text-gray-400 mt-1">관리자</p>
        </div>
        <nav className="flex-1 p-4 space-y-5 overflow-y-auto">
          {menuGroups.map((group) => (
            <div key={group.label}>
              <div className="px-4 mb-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">{group.label}</div>
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end
                    className={({ isActive }) =>
                      `block px-4 py-2 rounded-lg text-sm transition-colors ${
                        isActive
                          ? 'bg-jeju-brown text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-left"
          >
            로그아웃
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-8 overflow-auto">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="orders" element={<OrderManagement />} />
          <Route path="daily-orders" element={<DailyOrders />} />
          <Route path="ledger-history" element={<LedgerHistory />} />
          <Route path="coupang" element={<CoupangTool />} />
          <Route path="images" element={<ImageOptimizer />} />
          <Route path="tax" element={<TaxInvoiceManager />} />
          <Route path="vat" element={<VatFiling />} />
          <Route path="transaction" element={<TransactionStatement />} />
          <Route path="shipping-calendar" element={<ShippingCalendar />} />
          <Route path="envelope" element={<EnvelopeDesigner />} />
          {VENDORS.map(v =>
            v.custom === 'blueberry'
              ? <Route key={v.key} path={v.key} element={<BlueberryFarm />} />
              : <Route key={v.key} path={v.key} element={<VendorPage vendor={v} />} />
          )}
          {VENDORS.filter(v => v.settlement).map(v => (
            <Route key={`settlement-${v.key}`} path={`settlement/${v.key}`} element={<SettlementPage vendorCode={v.code} vendorName={v.name} />} />
          ))}
          <Route path="settlement/sundumbi" element={<SundumbiSettlement />} />
        </Routes>
      </main>
    </div>
    </UploadedFilesProvider>
  );
}

export default AdminLayout;
